import { createStyles, Group, Stack } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { getSupportedTabs } from '@repo/common/constants/integration/integrations.preferences';
import { Button, Text, Title } from '@repo/foundations';
import { useFormik } from 'formik';
import { isEmpty, isNil } from 'lodash-es';
import { useEffect, useImperativeHandle, useMemo } from 'react';
import * as Yup from 'yup';
import type { IIntegration } from '../../../../../api';
import { useAuthUser, useSuspenseFeatureAccess } from '../../../../../api';
import { useAiWorkspaceSettingEnabled } from '../../../../../hooks/useAiWorkspaceSettingEnabled';
import type { IntegrationSpec } from '../../../../../interfaces/IntegrationSpec';
import { useFeatureFlags } from '../../../../../utils/featureFlags';
import { IntegrationPreferences } from '../../../IntegrationPreferences';
import {
	cronValidationMessage,
	isValidCron,
} from '../../../IntegrationSchedule/utils';
import { IntegrationSchedulePicker } from '../../../IntegrationSchedulePicker';
import useIntegrationExtraction from '../../useIntegrationExtraction';

export type SyncStepActions = {
	submit: () => void;
};

interface SyncStepProps {
	integrationSpec: IntegrationSpec;
	integration: IIntegration;
	hideSubmit?: boolean;
	actionsRef?: React.RefObject<SyncStepActions>;
	onDone?: () => void;
	hideTitle?: boolean;
}

const useStyles = createStyles((theme) => ({
	dropdown: {},
	anchor: {
		fontSize: theme.other.typography.text.sm,
		weight: theme.other.typography.weight.semibold,
	},
	frame: {
		borderRadius: theme.other.borderRadius.md,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		background: theme.other.getColor('surface/primary/default'),
		padding: theme.spacing.md,
	},
}));

export function SyncStep({
	integrationSpec,
	integration,
	actionsRef,
	hideSubmit = false,
	onDone,
	hideTitle = false,
}: SyncStepProps) {
	const { classes, theme } = useStyles();
	const { user, workspace } = useAuthUser();
	const { allowFrequentSyncs } = useFeatureFlags();
	const { secodaCoreAccess } = useSuspenseFeatureAccess();
	const enableAi = useAiWorkspaceSettingEnabled();

	const preferences = useMemo(() => {
		const supportedTabs = getSupportedTabs(integrationSpec, {
			isAIEnabledWorkspace: enableAi,
		});
		return (
			secodaCoreAccess &&
			supportedTabs.find((tab) => tab.name === 'preferences')
		);
	}, [enableAi, integrationSpec, secodaCoreAccess]);

	const { extractionJobs, polling, handleRunExtraction } =
		useIntegrationExtraction({
			id: integration.id,
		});

	useEffect(() => {
		// start sync when component loads, skip if pull is disabled
		if (integrationSpec.value.disablePullMetadata) {
			return;
		}

		if (!extractionJobs?.length && !polling) {
			handleRunExtraction();
		}
	}, [
		extractionJobs,
		polling,
		handleRunExtraction,
		integrationSpec.value.disablePullMetadata,
	]);

	const formik = useFormik({
		initialValues: {
			scheduled_extractions_cron: '0 0 * * 6',
		},
		validationSchema: Yup.object({
			scheduled_extractions_cron: Yup.string().test(
				'is-valid-cron',
				(value) => cronValidationMessage(value.value || '', allowFrequentSyncs),
				(value) => isNil(value) || isValidCron(value, allowFrequentSyncs)
			),
		}),
		onSubmit: () => {
			trackEvent(
				'integration/schedule/update',
				{
					label: integration.name,
					type: integration.type,
				},
				user!,
				workspace!
			);
			onDone?.();
		},
	});

	useImperativeHandle(actionsRef, () => ({
		submit: formik.handleSubmit,
	}));

	return (
		<form onSubmit={formik.handleSubmit} autoComplete="off">
			<Stack spacing="2xl" pb={theme.spacing.xl}>
				{!hideTitle && (
					<Stack spacing="3xs">
						<Title size="xl">Your initial sync has started</Title>
						<Text size="sm" color="text/secondary/default">
							You’ll be notified when the sync is complete. In the meantime,
							here are some things you can do. You can always update these
							details later in your integration settings.
						</Text>
					</Stack>
				)}
				<Stack spacing="4xl">
					<Stack spacing="md">
						<Title size="md">Schedule</Title>
						<IntegrationSchedulePicker integrationId={integration.id} />
					</Stack>
					{preferences && (
						<Stack spacing="md">
							<Title size="md">Additional preferences</Title>
							<Stack spacing="3xs" className={classes.frame}>
								<IntegrationPreferences
									id={integration.id}
									preferenceOptions={preferences?.options || []}
								/>
							</Stack>
						</Stack>
					)}
				</Stack>
				{!hideSubmit && (
					<Group position="right">
						<Button
							type="submit"
							variant="primary"
							w="fit-content"
							disabled={!isEmpty(formik.errors.scheduled_extractions_cron)}
						>
							Done
						</Button>
					</Group>
				)}
			</Stack>
		</form>
	);
}
